import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { ProviderMark, RelationBadge, Tag } from "@/components/common/badges";
import { Page, PageTitle, Panel } from "@/components/common/page";
import { PageSkeleton } from "@/components/common/states";
import { q } from "@/data/queries";
import { features } from "@/lib/features";
import { formatNumber, relativeTime } from "@/lib/format";

const INVITE_CLASS: Record<string, string> = {
  accepted: "border-ok/30 bg-ok-soft/60 text-ok",
  queued: "border-wait/30 bg-wait-soft/60 text-wait",
  sent: "border-border text-muted-foreground",
  ignored: "border-border text-ink-faint",
  withdrawn: "border-border text-ink-faint",
};

/**
 * The social surface, behind `features.messaging` (off).
 *
 * Profiles, invitations and posts belong to the deferred account-based
 * channels, not to the launch scope — the launch-scope people screen is
 * Contacts. Kept, flagged off and unreachable, because it is the screen those
 * channels land in.
 */
export const Route = createFileRoute("/p/$project/people")({
  beforeLoad: () => {
    if (!features.messaging) throw notFound();
  },
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(q.profiles()),
      context.queryClient.ensureQueryData(q.socialInvites()),
      context.queryClient.ensureQueryData(q.posts()),
    ]),
  pendingComponent: PageSkeleton,
  component: PeoplePage,
});

function PeoplePage() {
  const { data: profiles } = useSuspenseQuery(q.profiles());
  const { data: invites } = useSuspenseQuery(q.socialInvites());
  const { data: posts } = useSuspenseQuery(q.posts());

  return (
    <Page wide>
      <PageTitle
        title="People"
        description="Profiles, invitations and posts on the social providers. Reads come from the connected account's own view, so what you can see is what that person can see."
      />

      <Panel
        title="Invitations"
        description="Connection requests, with what Aux0 is holding and why"
        bodyClassName="divide-y"
      >
        {invites.map((invite) => (
          <div key={invite.id} className="px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[13px] font-medium">{invite.profileName}</span>
              <Tag className={INVITE_CLASS[invite.status]}>{invite.status}</Tag>
              <span className="text-[11px] text-ink-faint">
                {invite.respondedAt
                  ? `responded ${relativeTime(invite.respondedAt)}`
                  : invite.sentAt
                    ? `sent ${relativeTime(invite.sentAt)}`
                    : "not sent yet"}
              </span>
            </div>
            {invite.heldBecause ? (
              <p className="mt-1 max-w-[76ch] text-[12.5px] leading-[1.5] text-wait">
                {invite.heldBecause}
              </p>
            ) : invite.note ? (
              <p className="mt-1 max-w-[76ch] text-[12.5px] leading-[1.5] text-muted-foreground">
                “{invite.note}”
              </p>
            ) : null}
          </div>
        ))}
      </Panel>

      <Panel title="Profiles" bodyClassName="divide-y">
        {profiles.map((profile) => (
          <div key={profile.id} className="flex flex-wrap items-start gap-3 px-4 py-3">
            <ProviderMark provider={profile.provider} className="mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13px] font-medium">{profile.name}</span>
                <RelationBadge relation={profile.relation} />
                {profile.requiresPremium ? (
                  <Tag mono className="border-warn/30 bg-warn-soft/60 text-warn">
                    premium
                  </Tag>
                ) : null}
              </div>
              <p className="mt-0.5 text-[12.5px] text-muted-foreground">
                {profile.headline}
              </p>
              <p className="mt-0.5 text-[11.5px] text-ink-faint">
                {profile.location}
                {profile.connections
                  ? ` · ${formatNumber(profile.connections)} connections`
                  : ""}
              </p>
            </div>
            <span className="mono-id shrink-0">{profile.handle}</span>
          </div>
        ))}
      </Panel>

      <Panel title="Posts" bodyClassName="divide-y">
        {posts.map((post) => (
          <article key={post.id} className="px-4 py-3.5">
            <div className="flex flex-wrap items-center gap-2">
              <ProviderMark provider={post.provider} size="sm" />
              <span className="text-[13px] font-medium">{post.authorName}</span>
              {post.viaApi ? (
                <Tag mono className="border-brand/30 bg-brand-soft/60 text-brand">
                  via API
                </Tag>
              ) : null}
              <span className="text-[11px] text-ink-faint">
                {relativeTime(post.at)}
              </span>
            </div>
            <p className="mt-2 max-w-[76ch] text-[13px] leading-[1.55] whitespace-pre-wrap">
              {post.text}
            </p>
            <p className="mt-2 flex gap-4 text-[11.5px] text-muted-foreground tabular">
              <span>{formatNumber(post.reactions)} reactions</span>
              <span>{formatNumber(post.comments)} comments</span>
              <span>{formatNumber(post.reposts)} reposts</span>
            </p>
          </article>
        ))}
      </Panel>
    </Page>
  );
}
